import React, { FC, MouseEvent, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  IconButton,
  InputBase,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChatMessage from "./ChatMessage";
import AudioGravador from "./AudioGravador";
import ChatAudioMessage from "./ChatAudioMessage";
import TypingAnimation from "./TypingAnimation";
import { ArrowUpward } from "@mui/icons-material";

export type Message = {
  from: "user" | "lis";
  text: string | JSX.Element;
  time: string;
  type: "text" | "audio" | "video" | "image" | "file";
  audioUrl?: string;
};

export interface ChatConversationProps {
  messages: Message[];
  input: string;
  setInput: (input: string) => void;
  setMessages: (messages: Message) => void;
  loading: boolean;
  setIsOpen: (isOpen: boolean) => void;
  avatarLisUrl: string;
  onMessageSent: () => void;
  onLinkClick: (linkUrl: string, linkText?: string) => void;
}

export const ChatConversation: FC<ChatConversationProps> = ({
  messages,
  input,
  setMessages,
  setInput,
  loading,
  setIsOpen,
  avatarLisUrl,
  onMessageSent,
  onLinkClick,
}) => {
  const [userScrolled, setUserScrolled] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollHeightRef = useRef<number>(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const chatUsedRef = useRef(false);

  const scrollToBottom = React.useCallback(() => {
    if (!userScrolled) {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  }, [userScrolled]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom =
      Math.abs(
        element.scrollHeight - element.scrollTop - element.clientHeight
      ) < 1;

    if (element.scrollTop < lastScrollHeightRef.current) {
      setUserScrolled(true);
    }

    if (isAtBottom) {
      setUserScrolled(false);
    }

    lastScrollHeightRef.current = element.scrollTop;
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      const isAtBottom =
        Math.abs(
          container.scrollHeight - container.scrollTop - container.clientHeight
        ) < 1;
      if (!userScrolled || isAtBottom) {
        scrollToBottom();
      }
    }
  }, [messages, loading, scrollToBottom, userScrolled]);

  useEffect(() => {
    if (!loading && chatUsedRef.current) {
      chatUsedRef.current = false;
      inputRef.current?.focus();
    }
  }, [loading]);

  const handleMessagesClick = (e: MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest("a");
    if (!anchor) return;

    onLinkClick(
      anchor.getAttribute("href") ?? "",
      anchor.textContent?.trim() || undefined
    );
  };

  const sendMessage = () => {
    window?.dispatchEvent(new Event("stopAllAudios"));
    if (!input.trim()) return;

    const now = new Date();
    const time = now.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    setMessages({ from: "user", text: input.trim(), time, type: "text" });
    onMessageSent();
    setInput("");

    chatUsedRef.current = true;
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: { xs: "center", sm: "flex-end" },
        width: "100vw",
      }}
    >
      <Card
        sx={{
          width: {
            xs: "90%",
            sm: "22.5rem",
          },
          height: "32.5rem",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: 4,
          bgcolor: "#E6F3FF",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#0A2F66",
            color: "white",
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              src={avatarLisUrl}
              alt="Lis"
              sx={{
                mr: 1,
                bgcolor: "#00384d",
                width: 47,
                height: 47,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontSize: "22px",
                lineHeight: "30px",
                letterSpacing: "1px",
                textAlign: "center",
                color: "#FDFDFD",
              }}
            >
              LIS
            </Typography>
          </Box>

          <IconButton onClick={() => setIsOpen(false)}>
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>
        </Box>
        {/* Chat messages */}
        <CardContent
          ref={messagesContainerRef}
          onScroll={handleScroll}
          onClick={handleMessagesClick}
          sx={{
            height: "100%",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            px: 2,
            py: 2,
            animation: "fadeInSlideUp 0.3s ease-in-out",
            "&::-webkit-scrollbar": {
              width: "8px",
              backgroundColor: "#DFE2E9",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#B0B8C1",
              borderRadius: "8px",
              minHeight: "24px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#8A99A8",
            },
            scrollbarWidth: "thin",
            scrollbarColor: "#B0B8C1 #DFE2E9",
            "& > *": {
              flexShrink: 0,
            },
          }}
        >
          <Box sx={{ flexGrow: 1 }} />
          {messages?.map((msg, index) => {
            const isLastLisMessage =
              msg.from === "lis" &&
              messages.slice(index + 1).every((m) => m.from !== "lis");

            if (msg.type === "audio" && msg.from === "lis") {
              return (
                <ChatAudioMessage
                  key={index}
                  msg={msg}
                  onDone={scrollToBottom}
                  avatarLisUrl={avatarLisUrl}
                />
              );
            }

            return (
              <ChatMessage
                key={index}
                msg={msg}
                isLastLisMessage={isLastLisMessage}
                onDone={scrollToBottom}
                avatarLisUrl={avatarLisUrl}
              />
            );
          })}
          {loading && <TypingAnimation />}
        </CardContent>

        {/* Input */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "white",
            px: 2,
            py: 1.5,
            mx: 2,
            mb: 2,
            borderRadius: "8px",
          }}
        >
          <InputBase
            inputRef={inputRef}
            fullWidth
            multiline
            minRows={1}
            maxRows={3}
            placeholder="Escreva aqui como posso te ajudar..."
            value={input}
            disabled={loading}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            sx={{
              fontSize: { xs: 14, sm: 16 },
              flex: 1,
              pr: 1,
              mr: { xs: 1, sm: 2 },
              bgcolor: "#FDFDFD",
              borderRadius: "8px",
              p: 1,
              "& .MuiInputBase-input": {
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: { xs: "14px", sm: "16px" },
                lineHeight: { xs: "20px", sm: "28px" },
                letterSpacing: "0px",
                color: "#28272C",
                overflowWrap: "break-word",
                wordBreak: "break-word",
                maxHeight: "120px",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#B0B8C1",
                  borderRadius: "4px",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: { xs: "14px", sm: "16px" },
                lineHeight: { xs: "20px", sm: "28px" },
                letterSpacing: "0px",
                color: "#6C7393",
                opacity: 1,
              },
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AudioGravador
              onTranscricao={(text) => {
                setMessages({
                  from: "user",
                  text,
                  type: "audio",
                  time: new Date().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                });
                onMessageSent();
                scrollToBottom();
              }}
              loading={loading}
            />
            <IconButton
              onClick={sendMessage}
              sx={{
                color: "#999EB0",
                transform: "rotate(0deg)",
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <ArrowUpward />
            </IconButton>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};
